import { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { Dropdown, DatePicker, ComboBox } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { appInfo } from "../../authConfig";
import Fuse from "fuse.js";
import i18next from "i18next";
import styles from "./Filters.module.css";

interface Props {
    corpIdList?: any;
    usersList?: any;
    handleApplyFilterClick: (filters: object) => void;
    resetFilters: (options: object) => void;
    applyFrontFilter: (values: object) => void;
    updateUserList: (corp_id: string, isFilterApplied: boolean) => void;
}

export const Filters = forwardRef<any, Props>(({ corpIdList, usersList, handleApplyFilterClick, applyFrontFilter, updateUserList, resetFilters }, ref) => {
    const [selectedFeedType, setSelectedFeedType] = useState<any | null>(null);
    const [selectedUsers, setSelectedUsers] = useState<any | null>(null);
    const [selectedCorpId, setSelectedCorpId] = useState<any | null>(null);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [isPrimaryFiltersChanged, setIsPrimaryFiltersChanged] = useState<boolean>(false);
    const [isPrimaryFiltersApplied, setIsPrimaryFiltersApplied] = useState<boolean>(false);
    const [isFiltersChanged, setIsFiltersChanged] = useState<boolean>(false);
    const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
    const [isCorpChanged, setIsCorpChanged] = useState<boolean>(false);

    const createDropdownOptions = useCallback((data: any) => Object.keys(data).map((key: string) => ({ key: key.toLowerCase(), text: data[key] })), []);

    const feedbackTypeOptions = useMemo(() => createDropdownOptions(appInfo.feedbacktype), [createDropdownOptions]);

    const [userListOptions, setUserListOptions] = useState<any>([]);
    const [corpListOptions, setCorpListOptions] = useState<any>([]);

    useImperativeHandle(ref, () => ({
        resetFields
    }));

    function resetFields() {
        setIsPrimaryFiltersApplied(false);
        resetFilters({ type: "all" });
        setIsFiltersChanged(false);
        setIsCorpChanged(false);
        setIsPrimaryFiltersChanged(false);
        setSelectedFeedType(null);
        setSelectedUsers(null);
        setSelectedCorpId(null);
        setStartDate(undefined);
        setEndDate(undefined);
        setIsFilterApplied(false);
    }

    const applyFilter = useCallback(() => {
        const filters = {
            selectedCorpId,
            selectedUsers,
            selectedFeedType,
            startDate: startDate?.toLocaleDateString("en-US"),
            endDate: endDate?.toLocaleDateString("en-US")
        };

        if (Object.values(filters).some(value => value !== null && value !== undefined)) {
            if (!isFilterApplied || isPrimaryFiltersChanged || (!isPrimaryFiltersApplied && isFiltersChanged && isCorpChanged)) {
                handleApplyFilterClick({
                    filters: filters,
                    isPrimaryFiltersChanged
                });
            } else {
                applyFrontFilter({
                    selectedCorpId,
                    selectedUsers
                });
            }
            setIsPrimaryFiltersChanged(false);
            setIsCorpChanged(false);
            setIsFilterApplied(true);
        }
    }, [
        selectedCorpId,
        selectedUsers,
        selectedFeedType,
        startDate,
        endDate,
        isFilterApplied,
        isPrimaryFiltersChanged,
        isPrimaryFiltersApplied,
        isFiltersChanged,
        isCorpChanged,
        handleApplyFilterClick,
        applyFrontFilter
    ]);

    const updateCorpIds = useCallback(
        (user_id: string | number | null) => {
            const corpUsers = usersList
                ?.filter((item: any) => item.key === user_id)
                .map((value: any) => {
                    return { key: value["corp_id"], text: value["corp_id"] };
                });
            setSelectedCorpId(corpUsers[0] ?? null);
        },
        [usersList]
    );

    const handleComboChange = useCallback(
        (type: string, item: any) => {
            switch (type) {
                case "corpIdList":
                    if (!item) {
                        setSelectedCorpId(null);
                        return;
                    }
                    if (item.key === "clear") {
                        resetFilters({ type: "corps" });
                        setSelectedCorpId(null);
                        setSelectedUsers(null);
                    } else {
                        setSelectedCorpId(item ?? null);
                        updateUserList(item?.key ?? null, !isPrimaryFiltersApplied && true);
                        setSelectedUsers(null);
                        setIsCorpChanged(true);
                    }
                    break;
                case "usersList":
                    if (!item) {
                        setSelectedUsers(null);
                        return;
                    }
                    if (item.key === "clear") {
                        resetFilters({ type: "user" });
                        setSelectedUsers(null);
                        setSelectedCorpId(null);
                    } else {
                        updateCorpIds(item?.key ?? null);
                        setSelectedUsers(item ?? null);
                    }
                    break;
                default:
                    break;
            }
            setIsFiltersChanged(true);
        },
        [resetFilters, updateUserList, isPrimaryFiltersApplied, updateCorpIds]
    );

    const handleFilterChange = useCallback((type: string, value: any) => {
        switch (type) {
            case "feedbackType":
                setSelectedFeedType(value);
                break;
            case "startDate":
                setStartDate(value);
                break;
            case "endDate":
                setEndDate(value);
                break;
            default:
                break;
        }
        setIsPrimaryFiltersChanged(true);
        setIsPrimaryFiltersApplied(true);
    }, []);

    const handleSearch = useCallback((initialData: any, value: string, setState: any) => {
        if (value.length === 0) {
            setState(initialData);
            return;
        }
        const options = {
            includeScore: true,
            includeMatches: true,
            ignoreLocation: true,
            threshold: 0.6,
            keys: ["text"]
        };

        // Fuse.js dual module format workaround:
        // The library exports both CJS (module.exports) and ESM (export default).
        // Jest uses CJS (default property), while Vite uses ESM (direct function).
        // This ensures compatibility across both test and runtime environments.
        const FuseCtor: typeof Fuse = typeof Fuse === "function" ? Fuse : (Fuse as { default: typeof Fuse }).default;
        const fuse = new FuseCtor(initialData, options);

        const results = fuse.search(value);
        const items = results.map(result => result.item);
        setState(items);
    }, []);

    useEffect(() => {
        setUserListOptions(usersList);
    }, [usersList]);

    useEffect(() => {
        setCorpListOptions(corpIdList);
    }, [corpIdList]);

    return (
        <div className={styles.filterContainer}>
            <DatePicker
                className={styles.datePicker}
                placeholder={i18next.t("filters.startDate")}
                formatDate={date => (date ? date.toLocaleDateString() : "")}
                onSelectDate={date => handleFilterChange("startDate", date || undefined)}
                value={startDate}
                maxDate={new Date()}
            />
            <DatePicker
                className={styles.datePicker}
                placeholder={i18next.t("filters.endDate")}
                formatDate={date => (date ? date.toLocaleDateString() : "")}
                onSelectDate={date => handleFilterChange("endDate", date || undefined)}
                value={endDate}
                minDate={startDate}
                maxDate={new Date()}
            />
            <ComboBox
                className="corpIdList"
                style={{ width: "100%", margin: "0" }}
                selectedKey={selectedCorpId?.key || null}
                placeholder={i18next.t("filters.corpId")}
                allowFreeform
                autoComplete="on"
                options={corpListOptions}
                onChange={(event, item: any) => handleComboChange("corpIdList", item)}
                onInputValueChange={text => handleSearch(corpIdList, text, setCorpListOptions)}
            />
            <ComboBox
                className="usersList"
                style={{ width: "100%", margin: "0" }}
                selectedKey={selectedUsers?.key || null}
                placeholder={i18next.t("filters.userId")}
                allowFreeform
                autoComplete="on"
                options={userListOptions}
                onChange={(event, item) => handleComboChange("usersList", item)}
                onInputValueChange={text => handleSearch(usersList, text, setUserListOptions)}
            />
            <Dropdown
                className="feedbackType"
                style={{ margin: "0" }}
                selectedKey={selectedFeedType?.key || null}
                onChange={(event, item) => handleFilterChange("feedbackType", item)}
                placeholder={i18next.t("filters.feedbackType")}
                options={feedbackTypeOptions}
            />
            <div className={styles.buttonContainer}>
                <Button
                    size="medium"
                    className={styles.resetButton}
                    title={i18next.t("filters.resetFilters")}
                    onClick={resetFields}
                    disabled={!isPrimaryFiltersApplied && !isFiltersChanged}
                >
                    {i18next.t("filters.resetFilters")}
                </Button>
                <Button
                    size="medium"
                    className={styles.applyButton}
                    title={i18next.t("filters.applyFilter")}
                    onClick={applyFilter}
                    disabled={!isPrimaryFiltersApplied && !isFiltersChanged}
                >
                    {i18next.t("filters.applyFilter")}
                </Button>
            </div>
        </div>
    );
});

Filters.displayName = "Filters";
